import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@mui/styles";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Grid from '@mui/material/Unstable_Grid2'; // Grid version 2
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import CircularProgress from "@mui/material/CircularProgress";
import PhoneIcon from "@mui/icons-material/PhoneAndroid";
import KeyIcon from "@mui/icons-material/VpnKey";
import ClearIcon from "@mui/icons-material/Clear";
import SaveIcon from "@mui/icons-material/SaveAlt";
import SectionAccordion from "components/SectionAccordion";

//for passkey
import auth from "../../services/Auth";

import moment from "moment";
import _cloneDeep from "lodash/cloneDeep";
import { default as _pick } from "lodash/pick";

const DATE_FORMAT = "MMM DD, YYYY";
const DATE_TIME_FORMAT = "MMM DD, YYYY hh:mm:ss A";

const useStyles = makeStyles((theme) => ({
  profileDialogClose: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
}));

const SectionActionButton = ({ children, ...rest }) => (
  <Button
    {...rest}
    variant="outlined"
    color="primary"
    style={{
      fontSize: "0.825rem",
      textTransform: "none",
      paddingLeft: 24,
      paddingRight: 24,
    }}
  >
    {children}
  </Button>
);

const Credential = ({ Icon, name, created, last_used }) => (
  <Box display="flex" marginTop={3} marginBottom={3}>
    <Box>
      <Icon fontSize="large" />
    </Box>
    <Box marginLeft={1.5}>
      <Typography style={{ fontWeight: 500 }}>{name || "<Unnamed>"}</Typography>
      <Typography>
        Created: {moment.utc(created).local().format(DATE_FORMAT) ?? "unknown"}
      </Typography>
      <Typography>
        Last used:{" "}
        {last_used
          ? moment.utc(last_used).local().format(DATE_TIME_FORMAT)
          : "never"}
      </Typography>
    </Box>
  </Box>
);

const ProfileDialog = (props) => {
  const [code, setCode] = useState("");
  const [codeE, setCodeE] = useState("");
  const [passkeyDialogOpen, setPasskeyDialogOpen] = useState("");
  const [isPasskeyAvailable, setIsPasskeyAvailable] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [authMessage, setAuthMessage] = useState("");
  const [authErr, setAuthErr] = useState("");
  const [detailsError, setDetailsError] = useState("");
  const [note, setNote] = useState("");
  const [authcode, setAuthcode] = useState("");
  const [dirty, setDirty] = useState({});
  const [busy, setBusy] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [messageContent, setMessageContent] = useState("");

  const classes = useStyles();

  const {
    refetchProfile,
    profile,
    setProfile,
    mode,
    open,
    changeProp,
    save,
    close,
    showVerifyEmail,
    verifyEmail,
    verifyPhone,
  } = props;

  useEffect(() => {
    (async () => {
      if (
        window.PublicKeyCredential &&
        (await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.())
      ) {
        setIsPasskeyAvailable(true);
      }
    })();
  }, []);

  const hasInterstacksAuthMethod = !profile
    ? false
    : (profile.auth_methods ?? ["interstacks"]).includes("interstacks");

  const isAuthcodeValid = useMemo(
    (code) => authcode.length === 6 && /^-?\d+$/.test(authcode),
    [authcode]
  );

  const showVerifyPhone =
    profile.phone &&
    mode !== "changed" &&
    profile.verified_phone !== profile.phone;
  //note: we don't use this at the moment since we don't yet allow email edit
  // const showVerifyEmail =
  //   profile.verified_email !== profile.email ||
  //   profile.new_email_addr;
  const showVerifiedEmail =
    profile.email &&
    profile.email === profile.verified_email &&
    mode === "edit";
  const showVerifiedPhone =
    profile.phone &&
    profile.phone === profile.verified_phone &&
    mode === "edit";

  const phoneWarning =
    profile.phone &&
    (!profile.phone.startsWith("+") || profile.phone.length < 12);
  const authorizerEnabled = profile?.authenticator_credentials?.length > 0;
  const authorizerEnabledText = authorizerEnabled
    ? " or Authenticator App"
    : "";

  const createPasskey = () => {
    setBusy(true);
    auth
      .passkeyRegister()
      .then(() => {
        refetchProfile(["passkey_credentials"]);
        setPasskeyDialogOpen("Passkey created.");
      })
      .catch(() => setPasskeyDialogOpen("Error Creating Passkey."))
      .finally(() => setBusy(false));
  };

  const PasskeyDialog = (props) => {
    const { open, onClose, content } = props;
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle align="center">Passkey</DialogTitle>
        <DialogContent dividers>
          <DialogContentText variant="body1" align="center">
            {content}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    );
  };
  const closePasskeyDialog = () => setPasskeyDialogOpen("");

  const createAuthenticator = () => {
    setAuthErr("");
    setAuthMessage("");
    setNote("");
    setAuthcode("");
    setBusy(true);
    auth
      .authenticatorRegister()
      .then((code) => setQrCode(code.qr_code))
      .catch(() => setAuthErr("Error in Authenticator Setup"))
      .finally(() => setBusy(false));
  };

  const verifyAuthenticator = () => {
    setAuthErr("");
    setAuthMessage("");
    setBusy(true);
    auth
      .authenticatorVerify(authcode, note)
      .then((result) => {
        if (result.Code === "BadRequestError") {
          setAuthErr(result.Message);
        } else {
          refetchProfile(["authenticator_credentials"]);
          setAuthMessage("Authenticator Setup Succeeded");
          setQrCode("");
          setAuthcode("");
          setNote("");
        }
      })
      .catch(() => { })
      .finally(() => setBusy(false));
  };

  const handleChangeProp = (key, value) => {
    if (!(key in dirty)) {
      setDirty((dirty) => ({
        ...dirty,
        [key]: _cloneDeep(profile[key]),
      }));
    }
    changeProp(key, value);
  };

  const handleSave = async () => {
    const update = _pick(profile, Object.keys(dirty));
    setBusy(true);
    const [rv, error] = await save(update);
    if (rv) {
      setDirty({});
      setMessageContent("Updates saved");
      setMessageOpen(true);
    }
    if (error) {
      setDetailsError(error);
    }
    setBusy(false);
  };

  const handleClose = () => {
    if (Object.keys(dirty).length > 0) {
      setMessageContent("Updates not saved. please save or cancel.");
      setMessageOpen(true);
    } else {
      close();
    }
  };

  const reset = () => {
    setProfile((profile) => {
      return {
        ...profile,
        ...dirty,
      };
    });
    setDirty({});
    setDetailsError("");
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open}>
      <DialogTitle>
        User Profile
        <IconButton
          onClick={handleClose}
          className={classes.profileDialogClose}
        >
          <ClearIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {profile ? (
          <>
            <SectionAccordion defaultExpanded title="Account details">
                <Grid container spacing={3}>
                  {profile.auth_methods?.length > 1 && (
                    <Grid xs={12}>
                      Your organization has multiple authentication methods
                      available for login.
                    </Grid>
                  )}
                  <Grid container xs={12} md={6} spacing={3}>
                    <Grid xs={12}>
                      <TextField
                        fullWidth
                        variant="standard"
                        label="Email"
                        value={profile.email}
                        disabled
                      />

                      {!!showVerifiedEmail && (
                        <div style={{ marginTop: 8 }}>Verified</div>
                      )}
                    </Grid>
                    {false && !!showVerifyEmail && (
                      <Grid xs={12}>
                        <div
                          style={{ display: "flex", justifyContent: "end" }}
                        >
                          <TextField
                            label="Email Code Received"
                            variant="standard"
                            value={codeE}
                            onChange={(e) => setCodeE(e.target.value)}
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={(e) => verifyEmail(codeE)}
                          >
                            Verify Email
                          </Button>
                        </div>
                      </Grid>
                    )}
                    {false && ( //later
                      <Grid xs={12}>
                        <TextField
                          fullWidth
                          variant="standard"
                          label="Change Email"
                          disabled
                          value={profile.new_email_addr}
                        />
                      </Grid>
                    )}
                    <Grid xs={12}>
                      <TextField
                        fullWidth
                        variant="standard"
                        label="Name"
                        value={profile.name}
                        onChange={(e) =>
                          handleChangeProp("name", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid xs={12}>
                      <TextField
                        fullWidth
                        variant="standard"
                        label="SMS Compatible Phone"
                        value={profile.phone}
                        onChange={(e) =>
                          handleChangeProp("phone", e.target.value)
                        }
                      />
                      {!!showVerifiedPhone && (
                        <div style={{ marginTop: 8 }}>Verified</div>
                      )}
                      {showVerifyPhone && (
                        <Grid xs={12}>
                          {!!phoneWarning && (
                            <div style={{ marginTop: 8 }}>
                              Phone number must have a country code with a
                              plus sign to work for SMS. Use +1 for US phones.
                              Make sure complete phone with all area codes is
                              specified.
                            </div>
                          )}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "end",
                              columnGap: 16,
                            }}
                          >
                            <TextField
                              label="SMS Code Received"
                              variant="standard"
                              value={code}
                              onChange={(e) => setCode(e.target.value)}
                            // style={{marginRight: 16}}
                            />
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={(e) => verifyPhone(code)}
                            >
                              Verify Phone
                            </Button>
                          </div>
                        </Grid>
                      )}
                    </Grid>
                    {hasInterstacksAuthMethod && (
                      <Grid xs={12}>
                        <FormControl variant="standard" fullWidth>
                          <InputLabel
                            shrink
                            id="mfa-label"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            Multifactor Delivery Preference
                          </InputLabel>
                          <Select
                            labelId="mfa-label"
                            autoWidth
                            value={profile.mfa ?? ""}
                            onChange={(e) =>
                              handleChangeProp("mfa", e.target.value)
                            }
                            displayEmpty
                          >
                            <MenuItem value="email">
                              Email{authorizerEnabledText}
                            </MenuItem>
                            <MenuItem value="sms">
                              SMS{authorizerEnabledText}
                            </MenuItem>
                            {authorizerEnabled && (
                              <MenuItem value="authcode">
                                Authenticator App Only
                              </MenuItem>
                            )}
                            <MenuItem value="">--not required--</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    {detailsError && (
                      <Grid xs={12}>
                        <Typography color="error">{detailsError}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
            </SectionAccordion>
            {hasInterstacksAuthMethod && (
              <SectionAccordion title={"Authenticator Apps ("+ profile.authenticator_credentials.length + ")"}>
                  <Grid container spacing={3}>
                    <Grid xs={12}>
                      <Box marginBottom={2}>
                        Set up one or more authenticator apps to create login
                        codes for use with MFA.
                      </Box>
                      {profile.authenticator_credentials?.map((c) => (
                        <Credential
                          key={c.created}
                          Icon={PhoneIcon}
                          name={c.name}
                          created={c.created}
                          last_used={c.last_used}
                        />
                      ))}
                      {authMessage && (
                        <Box marginBottom={3}>
                          <Typography color="primary">{authMessage}</Typography>
                        </Box>
                      )}
                      {!qrCode && (
                        <SectionActionButton
                          onClick={createAuthenticator}
                          disabled={busy}
                        >
                          + Set up authenticator
                        </SectionActionButton>
                      )}
                      {qrCode && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => setQrCode("")}
                          disabled={busy}
                        >
                          Cancel
                        </Button>
                      )}
                    </Grid>
                    {qrCode && (
                      <Grid xs={12} md={6}>
                        <div>
                          <img
                            src={`data:image/png;base64,${qrCode}`}
                            alt="QR Code"
                            width={200}
                          />
                          <br />
                          <p>Scan this QR code with your authenticator app.</p>
                          <Box>
                            Give this authenticator app a nickname (optional)
                          </Box>
                          <TextField
                            label="Authenticator nickname"
                            variant="standard"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            fullWidth
                          />
                          <Box marginTop={4}>Enter the code from the app:</Box>
                          <TextField
                            label="Code from Authenticator"
                            variant="standard"
                            value={authcode}
                            fullWidth
                            onChange={(e) => setAuthcode(e.target.value)}
                          />
                          <Box marginTop={2}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={verifyAuthenticator}
                              disabled={busy || !isAuthcodeValid}
                            >
                              Verify Authenticator
                            </Button>
                          </Box>
                        </div>
                      </Grid>
                    )}
                    {authErr && (
                      <Grid xs={12}>
                        <Typography color="error">{authErr}</Typography>
                      </Grid>
                    )}
                  </Grid>
              </SectionAccordion>
            )}
            {hasInterstacksAuthMethod && (
              <SectionAccordion title={"Passkeys (" + (profile.passkey_credentials?.length ?? 0) +")" }>
                {isPasskeyAvailable && (
                  <>
                    <Grid container spacing={3}>
                      <Grid xs={12}>
                        <Box marginBottom={2}>
                          Passkeys are available on this device. If configured
                          you may create Passkeys to enable password-less logins
                          from this device.
                        </Box>
                        {profile.passkey_credentials?.map((c) => (
                          <Credential
                            key={c.created}
                            Icon={KeyIcon}
                            name={c.created_by}
                            created={c.created}
                            last_used={c.last_used}
                          />
                        ))}
                        <SectionActionButton
                          onClick={createPasskey}
                          disabled={busy}
                        >
                          + Create a passkey
                        </SectionActionButton>
                      </Grid>
                    </Grid>
                    <PasskeyDialog
                      open={!!passkeyDialogOpen}
                      content={passkeyDialogOpen}
                      onClose={closePasskeyDialog}
                    />
                  </>
                )}
              </SectionAccordion>
            )}
          </>
        ) : (
          <Box display="flex" alignItems="center">
            <CircularProgress size="1.5rem" color="primary" />
            <Typography style={{ marginLeft: "0.5rem" }}>
              Loading profile...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        open={messageOpen}
        autoHideDuration={3000}
        onClose={() => setMessageOpen(false)}
        message={messageContent}
      />
      <DialogActions>
        {Object.keys(dirty).length > 0 && (
          <Box>
            <Button
              // disabled={toastOpen}
              // variant="contained"
              color="primary"
              onClick={handleSave}
              startIcon={<SaveIcon />}
              disabled={busy}
            >
              Save
            </Button>
            <Tooltip title="Current changes will be discarded" placement="top">
              <span>
                <Button
                  // disabled={toastOpen}
                  // variant="contained"
                  // color="primary"
                  style={{ color: "red" }}
                  onClick={reset}
                  disabled={busy}
                >
                  Cancel
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProfileDialog;
